import { useState, useMemo, useEffect } from "react";
import { useSearchStocks } from "../api/services/stock-analysis.hooks";

interface StockSearchOption {
  value: string;
  label: string;
  stock: {
    symbol: string;
    name: string;
  };
}

interface UseStockSearchOptions {
  debounceMs?: number;
  onSelect?: (symbol: string) => void;
}

interface UseStockSearchReturn {
  searchValue: string;
  searchOptions: StockSearchOption[];
  isLoading: boolean;
  handleSearch: (value: string) => void;
  handleSelect: (value: string) => void;
  clearSearch: () => void;
}

/**
 * Custom hook for stock search functionality with debouncing
 * @param options - Configuration options for the hook
 * @returns Search state and handlers
 */
export const useStockSearch = (
  options: UseStockSearchOptions = {}
): UseStockSearchReturn => {
  const { debounceMs = 300, onSelect } = options;
  const [searchValue, setSearchValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Search stocks with debounce
  const { data: searchResponse, isLoading } = useSearchStocks(searchQuery);

  // Transform search results to autocomplete options
  const searchOptions = useMemo<StockSearchOption[]>(() => {
    if (!searchResponse?.data) return [];
    return searchResponse.data.map((stock) => ({
      value: stock.symbol,
      label: `${stock.symbol} - ${stock.name}`,
      stock: stock,
    }));
  }, [searchResponse]);

  // Debounce search query
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue.length > 0) {
        setSearchQuery(searchValue);
      } else {
        setSearchQuery("");
      }
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [searchValue, debounceMs]);

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const handleSelect = (value: string) => {
    if (onSelect) {
      onSelect(value);
    }
    setSearchValue("");
    setSearchQuery("");
  };

  const clearSearch = () => {
    setSearchValue("");
    setSearchQuery("");
  };

  return {
    searchValue,
    searchOptions,
    isLoading,
    handleSearch,
    handleSelect,
    clearSearch,
  };
};

